import React from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ActionIcon, Text } from '@mantine/core';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	COLUMN_WIDTH,
	ROW_HEADER_HEIGHT,
} from '@dealstack/frontend/app/pages/Valuations/components/table/constants';
import { TextInputFilter } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/TextInputFilter';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';

interface Props {
	value: string;
	isLastColumn?: boolean;
	isSubColumn?: boolean;
	isLastSubColumn?: boolean;
	alignment?: 'start' | 'end';
	enableFilter?: boolean;
	enableExpanding?: boolean;
	filterValue?: string;
	onFilterChange?: (value: string) => void;
	filterPlaceholder?: string;
	filterComponent?: React.ReactElement;
	// Expanding props (only used if enableExpanding is true)
	isExpanded?: boolean;
	onToggleClick?: () => void;
	disableBorders?: boolean;
}

export function DefaultHeaderCell({
	value,
	isLastColumn,
	isSubColumn,
	isLastSubColumn,
	alignment,
	enableFilter = false,
	enableExpanding = false,
	filterValue = '',
	onFilterChange,
	filterPlaceholder = 'Filter...',
	filterComponent,
	isExpanded = false,
	onToggleClick,
	disableBorders = false,
}: Props) {
	const backgroundColor = isSubColumn
		? defaultTheme.v3.colors.gray[4]
		: defaultTheme.v3.colors.gray[2];

	const shouldShowBorder = !disableBorders && !isLastColumn && !(isSubColumn && !isLastSubColumn);
	const borderRight = shouldShowBorder ? `1px solid ${defaultTheme.v3.colors.gray[4]}` : 'none';

	const showFilterInput = enableFilter && (onFilterChange || filterComponent);

	let justifyContent: 'flex-start' | 'flex-end' | 'space-between';
	if (alignment === 'end') {
		justifyContent = 'flex-end';
	} else if (enableExpanding) {
		justifyContent = 'space-between';
	} else {
		justifyContent = 'flex-start';
	}

	// Always use column layout for consistency
	const containerStyle: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		minWidth: `${COLUMN_WIDTH}px`,
		overflow: 'hidden',
		backgroundColor,
		borderRight,
		borderBottom: disableBorders ? 'none' : `1px solid ${defaultTheme.v3.colors.gray[4]}`,
		minHeight: `${ROW_HEADER_HEIGHT}px`,
	};

	// Header row contains the text and optionally the expand icon
	const headerRowStyle: React.CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		justifyContent,
		gap: '0.5rem',
		padding: '0.5rem',
		minHeight: `${ROW_HEADER_HEIGHT}px`,
		width: '100%',
		boxSizing: 'border-box',
	};

	// Filter input row
	const inputRowStyle: React.CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		maxWidth: `${COLUMN_WIDTH}px`,
		padding: '0 0.5rem 0.5rem 0.5rem',
		overflow: 'hidden',
	};

	return (
		<div
			data-testid={makeComplexDataTestId(TEST_ELEMENT_IDS.DEFAULT_HEADER_CELL, value)}
			style={containerStyle}
		>
			<div style={headerRowStyle}>
				<Text size="B6Bold">{value}</Text>
				{enableExpanding && onToggleClick && (
					<ActionIcon
						data-testid={TEST_ELEMENT_IDS.DEFAULT_TOGGLE_HEADER_CELL}
						size="lg"
						variant="subtle"
						style={{ backgroundColor: 'transparent' }}
						onClick={onToggleClick}
						color="black.0"
					>
						<Icon
							variant={isExpanded ? 'caret-left' : 'caret-right'}
							size="lg"
							color={defaultTheme.v3.colors.black[0]}
						/>
					</ActionIcon>
				)}
			</div>

			{showFilterInput && (
				<div style={inputRowStyle}>
					{filterComponent ||
						(onFilterChange && (
							<TextInputFilter
								value={filterValue}
								onChange={onFilterChange}
								placeholder={filterPlaceholder}
								width="100%"
							/>
						))}
				</div>
			)}
		</div>
	);
}
